import type { Fetcher } from "../fetcher";
import type { DriverFilters, CreateDriverDto, UpdateDriverDto } from "./types";
import type { Driver } from "../entities/driver";
import type { ApiSingleResponse, ApiCollectionResponse } from "../types/api";

export function createDriversRepository(fetcher: Fetcher) {
  return {
    async list(filters: DriverFilters = {}): Promise<{
      items: Driver[];
      pagination: { total: number };
    }> {
      const params = new URLSearchParams();
      if (filters.page) params.append("page", String(filters.page));
      if (filters.limit) params.append("limit", String(filters.limit));
      if (filters.status) params.append("status", filters.status);
      if (filters.carrierId) params.append("carrierId", filters.carrierId);
      if (filters.search) params.append("search", filters.search);

      const query = params.toString();
      const url = query ? `/fleet/drivers?${query}` : "/fleet/drivers";

      const response = await fetcher<ApiCollectionResponse<Driver>>(url);
      return {
        items: response.data?.items ?? [],
        pagination: { total: response.data?.pagination?.total ?? 0 },
      };
    },

    async get(id: string): Promise<Driver> {
      const response = await fetcher<ApiSingleResponse<Driver>>(`/fleet/drivers/${id}`);
      return response.data;
    },

    async create(payload: CreateDriverDto): Promise<Driver> {
      const response = await fetcher<ApiSingleResponse<Driver>>("/fleet/drivers", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      return response.data;
    },

    async update(id: string, payload: UpdateDriverDto): Promise<Driver> {
      const response = await fetcher<ApiSingleResponse<Driver>>(`/fleet/drivers/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      return response.data;
    },

    async delete(id: string): Promise<{ id: string }> {
      const response = await fetcher<ApiSingleResponse<{ id: string }>>(
        `/fleet/drivers/${id}`,
        { method: "DELETE" },
      );
      return response.data ?? { id };
    },

    async getActiveShipment(id: string): Promise<unknown | null> {
      const response = await fetcher<ApiSingleResponse<unknown>>(
        `/fleet/drivers/${id}/active-shipment`,
      );
      return response.data ?? null;
    },
  };
}
